import React, { useState } from "react";
import "../scss/components/_rentreceipt.scss";

/* ---------- Inline icons ---------- */

const HouseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 11.5L12 4l8 7.5"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 10.5V19a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-8.5"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 20v-5h4v5" stroke="#fff" strokeWidth="2.2" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.5" y="5" width="17" height="16" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3.5 9.5h17" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const CardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="6" width="18" height="13" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 10.5h18" stroke="currentColor" strokeWidth="1.8" />
    <path d="M6.5 15h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const HashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.5 3.5l-3 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M17.5 3.5l-3 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M4 9h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M3 15h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.5 3.5h8l4 4v13a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M14.5 3.5v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M8 12.5h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M8 16h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20 6L9 17L4 12"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ---------- Main component ---------- */

const RentReceipt = ({
  pgName = "Boys Town PG",
  paidBy = "Tushar",
  amount = "30,000",
  paidOn = "16 Jul 2026",
  period = "1 Jul – 31 Jul 2026",
  periodLabel = "Rent Period",
  paymentMethod = "Online Transfer",
  receiptNo = "PGTR20260716A",
  receiptNumber = "#0149",
  receiptType = "RENT",
  onClose,
  // Advance & breakdown props
  advanceBalance = 0,
  rentAmount = 0,
  electricityAmount = 0,
  expenseAmount = 0,
  advanceAmount = 0,
  totalPaid = 0,
  showBreakdown = true,
}) => {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;

  const getReceiptLabel = () => {
    if (receiptType === 'PAYMENT') {
      return 'PAYMENT RECEIPT';
    }
    if (receiptType === 'ADVANCE') {
      return 'ADVANCE RECEIPT';
    }
    return `${receiptType} RECEIPT`;
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setVisible(false);
    }
  };

  // Check if we should show payment breakdown
  const hasBreakdownData = showBreakdown && (
    rentAmount > 0 || 
    electricityAmount > 0 || 
    expenseAmount > 0 || 
    advanceAmount > 0 ||
    advanceBalance > 0
  );

  // Calculate total from breakdown
  const breakdownTotal = rentAmount + electricityAmount + expenseAmount;
  const displayTotal = totalPaid > 0 ? totalPaid : amount.replace(/,/g, '');

  return (
    <div className="rent-receipt">
      <div className="rent-receipt__card">
        {/* ─── Header ────────────────────────────────────────── */}
        <div className="rent-receipt__header">
          <div className="rent-receipt__brand">
            <div className="rent-receipt__logo">
              <HouseIcon />
            </div>
            <div className="rent-receipt__brand-info">
              <span className="rent-receipt__pg-name">{pgName}</span>
              <span className="rent-receipt__receipt-type">{getReceiptLabel()}</span>
            </div>
          </div>
          <button
            type="button"
            className="rent-receipt__close"
            onClick={handleClose}
            aria-label="Close receipt"
          >
            <CloseIcon />
          </button>
        </div>

        {/* ─── Hero ──────────────────────────────────────────── */}
        <div className="rent-receipt__hero">
          <div className="rent-receipt__paid-by">
            <span className="rent-receipt__label">Paid By</span>
            <span className="rent-receipt__paid-by-name">{paidBy}</span>
          </div>
          <img className="rent-receipt__illustration" src="/assets/rent-receipt-building-illustration.png" alt="" />
        </div>

        <div className="rent-receipt__divider" />

        {/* ─── Amount ────────────────────────────────────────── */}
        <div className="rent-receipt__amount-section">
          <span className="rent-receipt__label">AMOUNT PAID</span>
          <div className="rent-receipt__amount-wrapper">
            <div className="rent-receipt__amount">
              <span className="rent-receipt__currency">₹</span>
              {amount}
            </div>
            <span className="rent-receipt__paid-on">Paid on {paidOn}</span>
          </div>
        </div>

        <div className="rent-receipt__divider" />

        {/* ─── Payment Details ──────────────────────────────── */}
        <div className="rent-receipt__details-section">
          <span className="rent-receipt__label rent-receipt__details-title">
            PAYMENT DETAILS
          </span>

          <div className="rent-receipt__detail-row">
            <span className="rent-receipt__detail-icon">
              <CalendarIcon />
            </span>
            <span className="rent-receipt__detail-label">{periodLabel}</span>
            <span className="rent-receipt__detail-value">{period}</span>
          </div>

          <div className="rent-receipt__detail-row">
            <span className="rent-receipt__detail-icon">
              <CardIcon />
            </span>
            <span className="rent-receipt__detail-label">Payment Method</span>
            <span className="rent-receipt__detail-value">{paymentMethod}</span>
          </div>

          <div className="rent-receipt__detail-row">
            <span className="rent-receipt__detail-icon">
              <HashIcon />
            </span>
            <span className="rent-receipt__detail-label">Reference No.</span>
            <span className="rent-receipt__detail-value">{receiptNo}</span>
          </div>

          <div className="rent-receipt__detail-row rent-receipt__detail-row--last">
            <span className="rent-receipt__detail-icon">
              <DocumentIcon />
            </span>
            <span className="rent-receipt__detail-label">Receipt No.</span>
            <span className="rent-receipt__detail-value">{receiptNumber}</span>
          </div>
        </div>

        {/* ─── Payment Breakdown (NEW) ──────────────────────── */}
        {hasBreakdownData && (
          <>
            <div className="rent-receipt__divider" />
            <div className="rent-receipt__details-section rent-receipt__breakdown-section">
              <span className="rent-receipt__label rent-receipt__details-title">
                PAYMENT BREAKDOWN
              </span>

              {/* Rent */}
              {rentAmount > 0 && (
                <div className="rent-receipt__detail-row rent-receipt__breakdown-row">
                  <span className="rent-receipt__detail-label">Rent</span>
                  <span className="rent-receipt__detail-value">₹{rentAmount.toLocaleString('en-IN')}</span>
                </div>
              )}

              {/* Electricity */}
              {electricityAmount > 0 && (
                <div className="rent-receipt__detail-row rent-receipt__breakdown-row">
                  <span className="rent-receipt__detail-label">Electricity</span>
                  <span className="rent-receipt__detail-value">₹{electricityAmount.toLocaleString('en-IN')}</span>
                </div>
              )}

              {/* Other Expenses */}
              {expenseAmount > 0 && (
                <div className="rent-receipt__detail-row rent-receipt__breakdown-row">
                  <span className="rent-receipt__detail-label">Other Expenses</span>
                  <span className="rent-receipt__detail-value">₹{expenseAmount.toLocaleString('en-IN')}</span>
                </div>
              )}

              {/* Advance Used (if any) */}
              {advanceAmount > 0 && (
                <div className="rent-receipt__detail-row rent-receipt__breakdown-row rent-receipt__breakdown-advance">
                  <span className="rent-receipt__detail-label">
                    <span className="rent-receipt__advance-icon">
                      <CheckIcon />
                    </span>
                    Advance Used
                  </span>
                  <span className="rent-receipt__detail-value rent-receipt__advance-value">
                    -₹{advanceAmount.toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              {/* Subtotal row (if multiple items) */}
              {(rentAmount > 0 || electricityAmount > 0 || expenseAmount > 0) && (
                <div className="rent-receipt__detail-row rent-receipt__breakdown-row rent-receipt__breakdown-subtotal">
                  <span className="rent-receipt__detail-label" style={{ fontWeight: '700' }}>
                    Subtotal
                  </span>
                  <span className="rent-receipt__detail-value" style={{ fontWeight: '700' }}>
                    ₹{breakdownTotal.toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              {/* Advance Balance (remaining) */}
              {advanceBalance > 0 && (
                <div className="rent-receipt__detail-row rent-receipt__breakdown-row rent-receipt__breakdown-balance">
                  <span className="rent-receipt__detail-label" style={{ fontWeight: '700', color: '#2563EB' }}>
                    <span className="rent-receipt__balance-icon">💰</span>
                    Remaining Advance Balance
                  </span>
                  <span className="rent-receipt__detail-value" style={{ fontWeight: '700', color: '#2563EB', fontSize: '1.1em' }}>
                    ₹{advanceBalance.toLocaleString('en-IN')}
                  </span>
                </div>
              )}

              {/* Note for advance usage */}
              {advanceAmount > 0 && (
                <div className="rent-receipt__breakdown-note">
                  <span className="rent-receipt__note-icon">ℹ️</span>
                  <span className="rent-receipt__note-text">
                    Advance deposit of ₹{advanceAmount.toLocaleString('en-IN')} has been adjusted against this payment
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {/* ─── Footer ────────────────────────────────────────── */}
        <div className="rent-receipt__footer">
          <span>Managed and powered by</span>
          <span className="rent-receipt__footer-brand">
            <img src="/assets/AapkaPG-logo.svg" alt="AapkaPG" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default RentReceipt;

// import React, { useRef, useState } from "react";
// import "../scss/components/_rentreceipt.scss"; // TODO: adjust path if scss files live elsewhere

// /* ---------- Inline icons ---------- */

// const HouseIcon = () => (
//   <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path
//       d="M4 11.5L12 4l8 7.5"
//       stroke="#fff"
//       strokeWidth="2.2"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//     <path
//       d="M6 10.5V19a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-8.5"
//       stroke="#fff"
//       strokeWidth="2.2"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//     <path d="M10 20v-5h4v5" stroke="#fff" strokeWidth="2.2" strokeLinejoin="round" />
//   </svg>
// );

// const CalendarIcon = () => (
//   <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <rect x="3.5" y="5" width="17" height="16" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
//     <path d="M3.5 9.5h17" stroke="currentColor" strokeWidth="1.8" />
//     <path d="M8 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//     <path d="M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//   </svg>
// );

// const CardIcon = () => (
//   <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <rect x="3" y="6" width="18" height="13" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
//     <path d="M3 10.5h18" stroke="currentColor" strokeWidth="1.8" />
//     <path d="M6.5 15h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//   </svg>
// );

// const HashIcon = () => (
//   <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path d="M9.5 3.5l-3 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//     <path d="M17.5 3.5l-3 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//     <path d="M4 9h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//     <path d="M3 15h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//   </svg>
// );

// const DocumentIcon = () => (
//   <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path
//       d="M6.5 3.5h8l4 4v13a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1z"
//       stroke="currentColor"
//       strokeWidth="1.8"
//       strokeLinejoin="round"
//     />
//     <path d="M14.5 3.5v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
//     <path d="M8 12.5h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//     <path d="M8 16h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//   </svg>
// );

// const CloseIcon = () => (
//   <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
//     <path
//       d="M6 6l12 12M18 6L6 18"
//       stroke="currentColor"
//       strokeWidth="2"
//       strokeLinecap="round"
//     />
//   </svg>
// );

// /* ---------- Main component ---------- */

// const RentReceipt = ({
//   pgName = "Boys Town PG",
//   paidBy = "Tushar",
//   amount = "30,000",
//   paidOn = "16 Jul 2026",
//   period = "1 Jul – 31 Jul 2026",
//   periodLabel = "Rent Period",
//   paymentMethod = "Online Transfer",
//   receiptNo = "PGTR20260716A",
//   receiptNumber = "#0149",
//   receiptType = "RENT",
//   onClose,
// }) => {
//   const [visible, setVisible] = useState(true);

//   if (!visible) return null;

//   const getReceiptLabel = () => {
//     if (receiptType === 'PAYMENT') {
//       return 'PAYMENT RECEIPT';
//     }
//     return `${receiptType} RECEIPT`;
//   };

//   const handleClose = () => {
//     if (onClose) {
//       onClose();
//     } else {
//       setVisible(false);
//     }
//   };

//   return (
//     <div className="rent-receipt">
//       <div className="rent-receipt__card">
//         <div className="rent-receipt__header">
//           <div className="rent-receipt__brand">
//             <div className="rent-receipt__logo">
//               <HouseIcon />
//             </div>
//             <div className="rent-receipt__brand-info">
//               <span className="rent-receipt__pg-name">{pgName}</span>
//               <span className="rent-receipt__receipt-type">{getReceiptLabel()}</span>
//             </div>
//           </div>
//           <button
//             type="button"
//             className="rent-receipt__close"
//             onClick={handleClose}
//             aria-label="Close receipt"
//           >
//             <CloseIcon />
//           </button>
//         </div>

//         <div className="rent-receipt__hero">
//           <div className="rent-receipt__paid-by">
//             <span className="rent-receipt__label">Paid By</span>
//             <span className="rent-receipt__paid-by-name">{paidBy}</span>
//           </div>
//           <img className="rent-receipt__illustration" src="/assets/rent-receipt-building-illustration.png" alt="" />
//         </div>

//         <div className="rent-receipt__divider" />

//         <div className="rent-receipt__amount-section">
//           <span className="rent-receipt__label">AMOUNT PAID</span>
//           <div className="rent-receipt__amount-wrapper">
//             <div className="rent-receipt__amount">
//               <span className="rent-receipt__currency">₹</span>
//               {amount}
//             </div>
//             <span className="rent-receipt__paid-on">Paid on {paidOn}</span>
//           </div>
//         </div>

//         <div className="rent-receipt__divider" />

//         <div className="rent-receipt__details-section">
//           <span className="rent-receipt__label rent-receipt__details-title">
//             PAYMENT DETAILS
//           </span>

//           <div className="rent-receipt__detail-row">
//             <span className="rent-receipt__detail-icon">
//               <CalendarIcon />
//             </span>
//             <span className="rent-receipt__detail-label">{periodLabel}</span>
//             <span className="rent-receipt__detail-value">{period}</span>
//           </div>

//           <div className="rent-receipt__detail-row">
//             <span className="rent-receipt__detail-icon">
//               <CardIcon />
//             </span>
//             <span className="rent-receipt__detail-label">Payment Method</span>
//             <span className="rent-receipt__detail-value">{paymentMethod}</span>
//           </div>

//           <div className="rent-receipt__detail-row">
//             <span className="rent-receipt__detail-icon">
//               <HashIcon />
//             </span>
//             <span className="rent-receipt__detail-label">Reference No.</span>
//             <span className="rent-receipt__detail-value">{receiptNo}</span>
//           </div>

//           <div className="rent-receipt__detail-row rent-receipt__detail-row--last">
//             <span className="rent-receipt__detail-icon">
//               <DocumentIcon />
//             </span>
//             <span className="rent-receipt__detail-label">Receipt No.</span>
//             <span className="rent-receipt__detail-value">{receiptNumber}</span>
//           </div>
//         </div>

//         <div className="rent-receipt__footer">
//           <span>Managed and powered by</span>
//           <span className="rent-receipt__footer-brand">
//             <img src="/assets/AapkaPG-logo.svg" alt="" />
//           </span>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default RentReceipt;